// A store's things at their addresses (02-DESIGN/03-console/01-the-console.md
// § the address; hq decision 0069): a type's page `stores/<store>/types/<type>`,
// an index's `stores/<store>/indexes/<index>`, and an item's
// `stores/<store>/items/<path>`, whose path keeps its slashes — `invoice/inv-1`
// is two segments of the address, each encoded on its own — so the route is a
// splat. Pure, so the addresses are tested without a router.

import { tenantPath } from "../../address/address.ts";

/** SETTINGS_TAB is the store page's tab a type's or an index's crumb goes back to, named in the link's state. */
export const SETTINGS_TAB = "settings";

/** storeAddress is a store's page. */
export function storeAddress(slug: string, store: string): string {
  return tenantPath(slug, "stores", store);
}

/** typeAddress is a type's page, under its store's. */
export function typeAddress(slug: string, store: string, type: string): string {
  return tenantPath(slug, "stores", store, "types", type);
}

/** indexAddress is an index's page, under its store's. */
export function indexAddress(slug: string, store: string, index: string): string {
  return tenantPath(slug, "stores", store, "indexes", index);
}

/** itemAddress is an item's page: its path's segments follow `items`, each encoded on its own. */
export function itemAddress(slug: string, store: string, path: string): string {
  return tenantPath(slug, "stores", store, "items", ...path.split("/"));
}

/**
 * pathOfSplat is the item's path from what the splat route matched: the
 * segments after `items/`, joined by their slashes, with no empty segment —
 * a trailing slash names no item of its own.
 */
export function pathOfSplat(splat: string | undefined): string {
  return (splat ?? "")
    .split("/")
    .filter((p) => p !== "")
    .join("/");
}

/** tabOf reads the tab a link asked the store's page to open on, from its navigation state: Settings or nothing. */
export function tabOf(state: unknown): typeof SETTINGS_TAB | undefined {
  return typeof state === "object" &&
    state !== null &&
    (state as Record<string, unknown>).tab === SETTINGS_TAB
    ? SETTINGS_TAB
    : undefined;
}

/** STORE_FORMS are the forms a store's page shows, each in its Settings tab's place. */
export const STORE_FORMS = ["settings", "define-type", "declare-index"] as const;
export type StoreForm = (typeof STORE_FORMS)[number];

/** storeFormTab is the tab a store's form belongs to — Settings, for every one — or nothing where no form of the page is open. */
export function storeFormTab(open: string | undefined): typeof SETTINGS_TAB | undefined {
  return (STORE_FORMS as readonly string[]).includes(open ?? "") ? SETTINGS_TAB : undefined;
}
